import { Component, DestroyRef, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { Tree } from '../column-picker/models/column-picker';
import { filter, startWith, tap } from 'rxjs';
import { MetadataService } from '../../../../../../services/metadata.service';
import { TreeBuilder } from '../column-picker/tree-builder';
import { openConfirmationDialog } from '../../../../../confirmation-popup/confirmation-popup';
import { MatDialog } from '@angular/material/dialog';
import { FilterItemComponent } from './components/filter-item/filter-item.component';
import { MetadataColumn, MetadataFilterParameter } from '../../../../../../models/metadata';
import { TabFilter } from '../../../../models/utils-tabs';
import { TableType } from '../../../../../../models/shipment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface FiltersForm {
  order: number;
  columnKey: string;
  filterKey: string;
  parameters: MetadataFilterParameter[];
}

@Component({
  selector: 'app-filters',
  standalone: true,
  templateUrl: './filters.component.html',
  imports: [MatButton, FilterItemComponent],
  styleUrl: './filters.component.scss'
})
export class FiltersComponent implements OnChanges, OnInit {
  @Input() filtersArray!: FormArray<FormGroup>;
  @Input() type!: TableType;
  @Input() set filters(value: TabFilter[] | undefined) {
    this.addUUId(value);
  }

  treeNodes: Tree[] = [];

  fetchedFilters?: TabFilter[];
  private treeBuilder = new TreeBuilder('filterable');
  private metadataService = inject(MetadataService);
  private matDialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  ngOnInit() {
    if (this.fetchedFilters) {
      this.fetchedFilters.forEach(filter => this.addNewFilter(filter));
    }
    this.filtersArray.valueChanges
      .pipe(
        startWith(this.filtersArray.getRawValue()),
        tap(formData => {
          this.treeNodes = this.updateColumnAvailability(formData as unknown as FiltersForm[]);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filtersArray'] && changes['type']) {
      this.metadataService
        .getFilteredOutMetadata()
        .pipe(
          tap(({ columnGroups, shipmentColumns, containerColumns, orderColumns, orderExceptionColumns }) => {
            const columnsConfig: { [key: string]: MetadataColumn[] } = {
              Shipments: shipmentColumns,
              Containers: containerColumns,
              Orders: orderColumns,
              OrderExceptions: orderExceptionColumns
            };
            const columns = columnsConfig[this.type];
            this.treeNodes = this.treeBuilder.buildTree(columnGroups, columns);
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    }
  }

  addNewFilter(filter?: TabFilter) {
    const numberOfControls = this.filtersArray.controls.length;
    const columnKeyControl = new FormControl(filter?.columnKey || null, Validators.required);
    const newGroup = new FormGroup({
      order: new FormControl(filter?.order || numberOfControls),
      columnKey: columnKeyControl,
      filterKey: new FormControl({ value: '', disabled: true }, Validators.required),
      parameters: new FormGroup({}),
      id: new FormControl(filter?.id ? filter.id : crypto.randomUUID(), { nonNullable: true })
    });
    this.filtersArray.push(newGroup);
    setTimeout(() => {
      newGroup.get('filterKey')?.patchValue(filter?.filterKey || '');
      if (!filter) {
        columnKeyControl.setErrors({ required: true });
      }
    });
  }

  deleteFilter(index: number) {
    openConfirmationDialog(this.matDialog, {
      title: 'Delete filter',
      message: 'Are you sure you want to delete this filter?'
    })
      .afterClosed()
      .pipe(
        filter(result => result === 'Confirmed'),
        tap(() => {
          this.filtersArray.removeAt(index);
          this.filtersArray.updateValueAndValidity();
          this.reorderFilters();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  getFilterBy(filterId: string) {
    return this.fetchedFilters?.find(({ id }) => id === filterId);
  }

  private updateColumnAvailability(formData: FiltersForm[], nodes?: Tree[]): Tree[] {
    const source = nodes ? nodes : this.treeNodes;
    return source.map(node => {
      if (node.children && node.children.length > 0) {
        return { ...node, children: this.updateColumnAvailability(formData, node.children) };
      } else {
        return node;
      }
    });
  }

  private reorderFilters() {
    this.filtersArray.controls.forEach((control, index) => {
      control.get('order')?.setValue(index);
    });
  }

  private addUUId(filters?: TabFilter[]) {
    if (filters) {
      this.fetchedFilters = filters.map(val => ({ ...val, id: crypto.randomUUID() }));
    }
  }
}
